import { useRef, useState, useCallback, useEffect } from "react";
import { MoveHorizontal } from "lucide-react";
import Reveal from "../Reveal";

const projects = [
  {
    title: "Toilet — Renovatie",
    tag: "Voor & Na",
    before: "/brand/ba/toilet-1-after-cropped.png",
    after: "/brand/ba/toilet-1-after-v3.png",
  },
  {
    title: "Badkamer — Renovatie",
    tag: "Voor & Na",
    before: "/brand/ba/bath-2-after-2-cropped.png",
    after: "/brand/ba/bath-2-new-after-edit-v3.png",
  },
  {
    title: "Badkamer & Toilet — Renovatie",
    tag: "Voor & Na",
    before: "/brand/ba/bath-3-after.jpeg",
    after: "/brand/ba/bath-3-after-edit-v3.png",
  },
];

function Slider({ before, after }) {
  const wrapRef = useRef(null);
  const [pos, setPos] = useState(50);
  const dragging = useRef(false);

  const move = useCallback((clientX) => {
    const rect = wrapRef.current?.getBoundingClientRect();
    if (!rect) return;
    const pct = ((clientX - rect.left) / rect.width) * 100;
    setPos(Math.max(0, Math.min(100, pct)));
  }, []);

  useEffect(() => {
    const onMove = (e) => {
      if (!dragging.current) return;
      const x = e.touches ? e.touches[0].clientX : e.clientX;
      move(x);
    };
    const onUp = () => (dragging.current = false);
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
    window.addEventListener("touchmove", onMove, { passive: true });
    window.addEventListener("touchend", onUp);
    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
      window.removeEventListener("touchmove", onMove);
      window.removeEventListener("touchend", onUp);
    };
  }, [move]);

  const start = (e) => {
    dragging.current = true;
    const x = e.touches ? e.touches[0].clientX : e.clientX;
    move(x);
  };

  return (
    <div
      ref={wrapRef}
      data-testid="ba-slider"
      className="relative w-full aspect-[4/5] md:aspect-[16/10] overflow-hidden bg-black select-none cursor-ew-resize"
      onMouseDown={start}
      onTouchStart={start}
    >
      {/* AFTER (background — full) */}
      <img
        src={after}
        alt="Na renovatie"
        draggable={false}
        className="absolute inset-0 w-full h-full object-cover"
      />

      {/* BEFORE (clipped) */}
      <div
        className="absolute inset-0 overflow-hidden"
        style={{ width: `${pos}%` }}
      >
        <img
          src={before}
          alt="Voor renovatie"
          draggable={false}
          className="absolute inset-0 h-full w-auto min-w-full max-w-none object-cover"
          style={{ width: wrapRef.current ? `${wrapRef.current.offsetWidth}px` : "100%" }}
        />
      </div>

      {/* Labels */}
      <div className="absolute top-4 left-4 md:top-6 md:left-6 bg-black/70 backdrop-blur-md px-3 py-1.5 text-[10px] md:text-xs uppercase tracking-[0.22em] text-white font-bold">
        Voor
      </div>
      <div className="absolute top-4 right-4 md:top-6 md:right-6 bg-[#1E5BC6] px-3 py-1.5 text-[10px] md:text-xs uppercase tracking-[0.22em] text-white font-bold">
        Na
      </div>

      {/* Divider + handle */}
      <div
        className="absolute top-0 bottom-0 w-0.5 bg-white pointer-events-none"
        style={{ left: `${pos}%`, transform: "translateX(-50%)" }}
      >
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-11 h-11 md:w-14 md:h-14 bg-white shadow-xl flex items-center justify-center pointer-events-auto">
          <MoveHorizontal
            className="text-[#1E5BC6]"
            size={20}
            strokeWidth={2.2}
          />
        </div>
      </div>
    </div>
  );
}

export default function BeforeAfter() {
  return (
    <section
      id="voor-na"
      data-testid="beforeafter-section"
      className="bg-white py-24 md:py-32"
    >
      <div className="max-w-7xl mx-auto px-5 md:px-8">
        <Reveal>
          <div className="max-w-3xl mb-14 md:mb-16">
            <span className="tag-overline">Voor & Na</span>
            <h2 className="mt-4 text-5xl md:text-6xl text-[#0A0A0A] leading-[1.02] uppercase tracking-tight">
              Het verschil
              <br />
              <span className="text-[#1E5BC6]">spreekt boekdelen.</span>
            </h2>
            <p className="mt-6 text-lg text-[#5A5A5A] max-w-2xl">
              Sleep de schuif om te zien hoe wij een oude ruimte transformeren
              in een moderne, tijdloze uitstraling. Zelfde ruimte, nieuwe
              wereld.
            </p>
          </div>
        </Reveal>

        <div className="space-y-16">
          {projects.map((p, i) => (
            <Reveal key={p.title} delay={i * 90}>
              <div className="border border-[#E5E7EB] overflow-hidden bg-[#F5F7FB]">
                <Slider before={p.before} after={p.after} />
                <div className="px-6 md:px-10 py-6 md:py-7 flex flex-col md:flex-row md:items-center md:justify-between gap-3 border-t border-[#E5E7EB]">
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.28em] text-[#1E5BC6] font-bold">
                      {p.tag}
                    </div>
                    <div className="mt-2 text-2xl md:text-3xl uppercase tracking-tight text-[#0A0A0A] font-extrabold">
                      {p.title}
                    </div>
                  </div>
                  <div className="text-xs uppercase tracking-[0.18em] text-[#5A5A5A] font-semibold">
                    Sleep om te vergelijken →
                  </div>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}
